import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@mui/styles";
import { connect } from "react-redux";
import {
  setSelectedArticleTypeId,
  createArticleType,
  updateArticleType,
} from "redux/index";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import SomuraTextField from "Components/SomuraTextField";
import SomuraAutocomplete from "Components/SomuraAutocomplete";
import SomuraSwitchField from "Components/SomuraSwitchField";
import CancelButton from "Components/Buttons/CancelButton";
import SaveButton from "Components/Buttons/SaveButton";
import DeleteButton from "Components/Buttons/DeleteButton";

const useStyles = makeStyles((theme) => ({
  dialog: {
    backgroundColor: theme.palette.background.scrollBar,
  },
  dialogTitle: {
    width: "30rem",
    padding: "4px 10px",
    fontWeight: "200",
    color: theme.palette.text.screenTitle,
  },
  dialogText: {
    color: theme.palette.text.calendars,
  },
  dialogContent: {
    padding: "10px 10px 6px 10px",
    backgroundColor: theme.palette.dialog.content,
  },
  dialogActions: {
    padding: "0 8px",
  },
}));

const mapStateToProps = (state) => {
  const types = state.articles.ArticleTypes;
  const selectedId = state.articles.selectedArticleTypeId;
  return { types, selectedId };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setSelectedId: (id) => dispatch(setSelectedArticleTypeId(id)),
    create: (values) => dispatch(createArticleType(values)),
    update: (values) => dispatch(updateArticleType(values)),
  };
};

const TypeDialog = ({ types, selectedId, typeId, create, update, close }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const emptyState = {
    id: "0",
    articleTypeName: "",
    needsInventoryNumber: false,
    parentId: "0",
  };
  const [state, setState] = useState(emptyState);
  const [valid, setValid] = useState(false);

  const typeSchema = Yup.object().shape({
    articleTypeName: Yup.string().required(t("Required")),
  });

  var typeOptions = types
    .filter((AT) => AT.id !== selectedId)
    .map((AT) => ({
      label: AT.articleTypeName,
      value: AT.id,
    }));
  typeOptions.unshift({ label: "", value: "0" });

  const handleClose = () => {
    close();
  };

  const handleSubmit = () => {
    if (state.id === "0") {
      // Create new model
      create(state);
    } else {
      // Update model
      update(state);
    }
    handleClose();
  };

  const handleDelete = () => {
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setState({
      ...state,
      [name]: value,
    });
  };

  const handleAutoCompleteChange = (params) => {
    const name = params.name;
    const value = params.value === "" ? "0" : params.value;
    setState({ ...state, [name]: value });
  };

  const handleSwitchChange = (event) => {
    const name = event.target.name;
    const value = event.target.checked;
    setState({
      ...state,
      [name]: value,
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validate = useCallback(async () => {
    setValid(await typeSchema.isValid(state));
  });

  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (selectedId > 0) {
      setState(types.find((T) => T.id === selectedId));
    } else {
      setState(emptyState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return (
    <div>
      <Dialog
        open={true}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose(event, reason);
          }
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {selectedId > 0
            ? t("Article type") + ": " + state.modelName
            : t("New article type")}
        </DialogTitle>
        <DialogContent className="dialogContent">
          <div className={classes.dialogContent}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <SomuraTextField
                  label={t("Article type")}
                  type="text"
                  name="articleTypeName"
                  value={state.articleTypeName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <SomuraSwitchField
                  name="needsInventoryNumber"
                  checked={state.needsInventoryNumber}
                  onChange={handleSwitchChange}
                  label={t("Needs inventory number")}
                />
              </Grid>
              <Grid item xs={12}>
                <SomuraAutocomplete
                  name="parentId"
                  label={t("Parent type")}
                  options={typeOptions}
                  onChange={(values) => handleAutoCompleteChange(values)}
                  value={state.parentId}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <CancelButton onClick={handleCancel} />
          {selectedId > 0 && (
            <DeleteButton
              onClick={handleDelete}
              disabled={parseInt(state.numAssigned) > 0}
            />
          )}
          <SaveButton disabled={!valid} onClick={handleSubmit} />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TypeDialog);
